import React, { useEffect, useState } from "react";
import PageContainer from "../../containers/PageContainer";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CardDetails from "../../components/customers/Payment/CardDetails";
import { getUser, createPaymentIntent } from "../../utils/supabase";

import OrderTable from "../../components/customers/Payment/OrderTable";
import { useOrder } from "../../context/Order.context";
import { useNavigate } from "react-router-dom";
import { Separator } from "../../components/ui/seperator";
import env from "../../config/env";

type Props = {};

const Payment = (props: Props) => {
  const [clientSecret, setClientSecret] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState<boolean>(true);
  const [userDetails, setUserDetails] = useState<any>({
    firstName: "",
    lastName: "",
    email: "",
    auth_id: "",
  });
  const [billingAddress, setBillingAddress] = useState({
    line1: "",
    city: "",
    state: "",
    postal_code: "",
  });
  const [saveCustomer, setSaveCustomer] = useState<boolean>(false);

  const { order } = useOrder();
  const navigate = useNavigate();

  // Load Stripe with the appropriate key based on environment
  const stripePromise = loadStripe(env.stripe.publishableKey as string);

  useEffect(() => {
    const initializePayment = async () => {
      try {
        const user = await getUser();

        if (!user || !user[0]) {
          console.error("❌ Payment Error: Unable to load user information", {
            timestamp: new Date().toISOString(),
            location: "Payment.tsx - initializePayment",
            reason: "User data not found",
          });
          navigate("/customer/new-document", { replace: true });
          return;
        }

        console.log("✅ User loaded successfully:", {
          email: user[0].email,
          name: `${user[0].first_name} ${user[0].last_name}`,
        });

        setUserDetails({
          firstName: user[0].first_name,
          lastName: user[0].last_name,
          email: user[0].email,
          auth_id: user[0].auth_id,
        });

        console.log("🔄 Creating payment intent...", {
          price: order?.price || 100,
          description: order?.product || "",
          environment: env.isDevelopment ? "development" : "production",
        });

        const data = await createPaymentIntent({
          dev: env.isDevelopment,
          price: order?.price ? order.price : 100,
          description: order?.product ? order.product : "",
          createCustomer: true,
          email: user[0].email,
          name: `${user[0].first_name} ${user[0].last_name}`,
          auth_id: user[0].auth_id,
        });

        console.log("✅ Payment Intent created successfully:", {
          clientSecret: data.clientSecret ? "***" + data.clientSecret.slice(-4) : "N/A",
          timestamp: new Date().toISOString(),
        });

        setClientSecret(data.clientSecret);
        setIsCreatingPaymentIntent(false);
        setIsLoading(false);
      } catch (error: any) {
        console.error("❌ Payment Initialization Error:", {
          timestamp: new Date().toISOString(),
          location: "Payment.tsx - initializePayment",
          error: error?.message || "Unknown error",
          response: error?.response?.data || null,
          stack: error?.stack,
        });
        setIsCreatingPaymentIntent(false);
        setIsLoading(false);
      }
    };

    initializePayment();
  }, []);

  const appearance = {
    theme: "stripe",
  };
  const options = {
    clientSecret,
    appearance,
  };

  return (
    <PageContainer pageTitle="Payment">
      {isCreatingPaymentIntent && (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-xl font-semibold text-jd-grey">
            Preparing payment...
          </div>
        </div>
      )}
      {!isLoading && !isCreatingPaymentIntent && (
        <div className="flex flex-col gap-y-8 pt-4">
          <div className="flex flex-row w-full">
            <h4 className="font-semibold text-jd-grey text-xl">
              Billing Details
            </h4>
          </div>
          <div className="flex flex-row max-sm:flex-col w-full gap-x-4">
            <div className="flex flex-col gap-y-2 max-sm:w-full w-1/3">
              <Label>First Name</Label>
              <Input
                type="text"
                value={userDetails.firstName}
                placeholder="First Name"
              />
            </div>
            <div className="flex flex-col gap-y-2 max-sm:w-full w-1/3">
              <Label>Last Name</Label>
              <Input
                type="text"
                value={userDetails.lastName}
                placeholder="Last Name"
              />
            </div>
          </div>
          <div className="flex flex-row max-sm:flex-col w-full gap-x-4">
            <div className="flex flex-col gap-y-2 max-sm:w-full w-1/3">
              <Label>Email</Label>
              <Input
                type="text"
                value={userDetails.email}
                placeholder="john@email.com"
              />
            </div>
          </div>
          <div className="flex flex-col gap-y-2">
            <Label>Street Number and Name</Label>
            <Input
              type="text"
              placeholder="42 Wallaby Way"
              value={billingAddress.line1}
              onChange={(e) =>
                setBillingAddress({ ...billingAddress, line1: e.target.value })
              }
            />
          </div>
          <div className="flex flex-row max-sm:flex-col gap-y-2 gap-x-4">
            <div className="flex flex-col gap-y-2 max-sm:w-full w-6/12">
              <Label>Suburb/Town</Label>
              <Input
                type="text"
                placeholder="Randwick"
                value={billingAddress.city}
                onChange={(e) =>
                  setBillingAddress({ ...billingAddress, city: e.target.value })
                }
              />
            </div>
            <div className="flex flex-col gap-y-2 max-sm:w-full w-3/12">
              <Label>State</Label>
              <Select
                value={billingAddress.state}
                onValueChange={(value) =>
                  setBillingAddress({ ...billingAddress, state: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a state" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="NSW">NSW</SelectItem>
                    <SelectItem value="VIC">VIC</SelectItem>
                    <SelectItem value="QLD">QLD</SelectItem>
                    <SelectItem value="ACT">ACT</SelectItem>
                    <SelectItem value="SA">SA</SelectItem>
                    <SelectItem value="WA">WA</SelectItem>
                    <SelectItem value="NT">NT</SelectItem>
                    <SelectItem value="TAS">TAS</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-y-2 max-sm:w-full w-3/12">
              <Label>Postcode</Label>
              <Input
                type="text"
                placeholder="2000"
                value={billingAddress.postal_code}
                onChange={(e) =>
                  setBillingAddress({
                    ...billingAddress,
                    postal_code: e.target.value,
                  })
                }
              />
            </div>
          </div>
          <Separator />
          <div className="flex flex-row w-full pt-4">
            <h4 className="font-semibold text-jd-grey text-xl">
              Order Details
            </h4>
          </div>
          <div className="flex flex-row max-sm:flex-col-reverse w-full gap-x-4">
            <div className="flex flex-col gap-y-2 max-md:w-2/3  max-sm:w-full w-1/3">
              {clientSecret && (
                <Elements
                  options={{
                    clientSecret: clientSecret,
                    appearance: { theme: "stripe" },
                  }}
                  stripe={stripePromise}
                >
                  <CardDetails
                    clientSecret={clientSecret}
                    billingAddress={billingAddress}
                  />
                </Elements>
              )}
            </div>
            <div className="flex flex-col gap-y-2 max-sm:w-full max-sm:pb-8 w-2/3">
              <OrderTable order={order} />
            </div>
          </div>
        </div>
      )}
    </PageContainer>
  );
};

export default Payment;
